(() => {
  if (!customElements.get('kk-gift-gallery')) {
    const galleries = new Map();
    const gifts = {
      bag: { title: 'A hand-embroidered bag', caption: 'A complimentary hand-embroidered Kashmiri bag accompanies your order.', example: '', views: [['bag', 'Bag']] },
      note: { title: 'A message, handwritten', caption: 'Your personal gift message, handwritten on your behalf.', example: 'Example handwriting shown.', views: [['note', 'Handwritten side'], ['cover', 'Floral cover']] },
      calligraphy: { title: 'Their name in calligraphy', caption: 'The recipient’s name, handwritten in Arabic or English by Aisha Manzoor.', example: 'Example names shown.', views: [['arabic', 'Arabic'], ['english', 'English'], ['reverse', 'Reverse']] }
    };
    customElements.define('kk-gift-gallery', class extends HTMLElement {
      connectedCallback() { queueMicrotask(() => { if (this.isConnected) this.init(); }); }
      disconnectedCallback() {
        this.events?.abort();
        this.resize?.disconnect();
        if (this.dialog?.open) this.dialog.close();
      }
      init() {
        this.events?.abort();
        this.resize?.disconnect();
        this.events = new AbortController();
        this.state = galleries.get(this.dataset.instance) || { group: 'bag', view: 0 };
        this.stage = this.querySelector('[data-gift-stage]');
        this.views = this.querySelector('[data-gift-views]');
        this.dialog = this.querySelector('dialog');
        this.lightboxStage = this.querySelector('[data-gift-lightbox-stage]');
        this.addEventListener('click', event => {
          const button = event.target.closest('button');
          if (!button || !this.contains(button)) return;
          if (button.dataset.giftGroup) {
            this.state = { group: button.dataset.giftGroup, view: 0 };
            this.render();
          } else if (button.hasAttribute('data-gift-view')) {
            this.state.view = Number(button.dataset.giftView);
            this.showPhoto();
          } else if (button.hasAttribute('data-gift-enlarge')) {
            const image = this.stage.querySelector('img').cloneNode(true);
            image.sizes = '90vw';
            image.loading = 'eager';
            this.lightboxStage.replaceChildren(image);
            this.querySelector('[data-gift-lightbox-title]').textContent = gifts[this.state.group].title;
            this.dialog.showModal();
            this.fit(this.lightboxStage);
          } else if (button.hasAttribute('data-gift-close')) {
            this.dialog.close();
          }
        }, { signal: this.events.signal });
        this.resize = new ResizeObserver(() => { this.fit(this.stage); this.fit(this.lightboxStage); });
        this.resize.observe(this.stage);
        this.resize.observe(this.lightboxStage);
        this.render();
      }
      fit(stage) {
        const image = stage.querySelector('.kk-gift-rotated');
        if (!image || !stage.clientWidth || !stage.clientHeight) return;
        image.style.width = `${stage.clientHeight}px`;
        image.style.height = `${stage.clientWidth}px`;
      }
      render() {
        const gift = gifts[this.state.group];
        this.querySelector('[data-gift-title]').textContent = gift.title;
        this.querySelector('[data-gift-caption]').textContent = gift.caption;
        const example = this.querySelector('[data-gift-example]');
        example.textContent = gift.example;
        example.hidden = !gift.example;
        this.querySelectorAll('[data-gift-group]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.giftGroup === this.state.group)));
        this.views.replaceChildren();
        this.views.hidden = gift.views.length < 2;
        gift.views.forEach((view, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'kk-gift-view';
          button.dataset.giftView = String(index);
          button.textContent = view[1];
          this.views.appendChild(button);
        });
        this.showPhoto();
      }
      showPhoto() {
        const gift = gifts[this.state.group];
        const [key, label] = gift.views[this.state.view];
        const image = this.querySelector(`template[data-gift-photo="${key}"]`).content.querySelector('img').cloneNode(true);
        this.stage.replaceChildren(image);
        this.views.querySelectorAll('button').forEach((button, index) => button.setAttribute('aria-pressed', String(index === this.state.view)));
        this.querySelector('[data-gift-status]').textContent = `${gift.title}, ${label}`;
        galleries.set(this.dataset.instance, this.state);
        this.fit(this.stage);
      }
    });
  }
  if (!customElements.get('kk-gift-fields')) {
    // Memory is scoped to this page and product; no personal details enter local storage.
    const drafts = new Map();
    customElements.define('kk-gift-fields', class extends HTMLElement {
      connectedCallback() { queueMicrotask(() => { if (this.isConnected) this.init(); }); }
      disconnectedCallback() {
        this.captureDraft();
        this.events?.abort();
      }
      init() {
        this.events?.abort();
        this.events = new AbortController();
        this.toggle = this.querySelector('[data-gift-enable]');
        this.fields = this.querySelector('[data-gift-inputs]');
        this.nameInput = this.querySelector('[data-gift-name]');
        this.language = this.querySelector('[data-gift-language]');
        this.message = this.querySelector('[data-gift-message]');
        const draft = drafts.get(this.dataset.instance);
        if (draft) {
          this.toggle.checked = draft.enabled;
          this.nameInput.value = draft.name;
          this.language.value = draft.language;
          this.message.value = draft.message;
        }
        this.toggle.disabled = false;
        this.addEventListener('change', () => this.sync(), { signal: this.events.signal });
        this.addEventListener('input', () => this.sync(), { signal: this.events.signal });
        this.nameInput.addEventListener('input', () => this.nameInput.setCustomValidity(this.nameInput.value.trim() ? '' : 'Please enter the recipient’s name.'), { signal: this.events.signal });
        this.nameInput.addEventListener('invalid', () => { this.fields.hidden = false; }, { signal: this.events.signal });
        const form = document.getElementById(this.dataset.form);
        form?.addEventListener('reset', () => queueMicrotask(() => { drafts.delete(this.dataset.instance); this.sync(); }), { signal: this.events.signal });
        this.sync();
      }
      sync() {
        this.captureDraft();
        const enabled = this.toggle.checked;
        this.fields.hidden = !enabled;
        this.fields.disabled = !enabled;
        this.nameInput.required = enabled;
        if (!enabled) this.nameInput.setCustomValidity('');
        this.toggle.setAttribute('aria-expanded', String(enabled));
      }
      captureDraft() {
        if (!this.toggle || !this.nameInput || !this.language || !this.message) return;
        // Keep a page-scoped draft even when this section is switched off or replaced.
        drafts.set(this.dataset.instance, { enabled: this.toggle.checked, name: this.nameInput.value, language: this.language.value, message: this.message.value });
      }
    });
  }
})();
